import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // KEIN pointerleave mehr – wir wollen nicht, dass es beim Rausfahren zugeht

    // Klick auf Top-Level Menüpunkt
    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const item = findMenuItem(event.target);
        if (!item) return;

        // Nur Menüpunkte mit echtem Submenü abfangen
        const submenu = findSubmenu(item);
        const hasSubmenu = !!submenu;

        if (!hasSubmenu) {
          // z.B. "Geschenke" ohne Dropdown -> normaler Link
          return;
        }

        // Dropdown-Item -> wir übernehmen den Klick
        event.preventDefault();

        const isActive = this.#state.activeItem === item;

        if (isActive) {
          // Zweiter Klick -> schließen
          this.#deactivate(item);
        } else {
          // Erster Klick -> öffnen
          this.activate(
            /** @type {MouseEvent} */ (event)
          );
        }
      },
      { signal: this.#abortController.signal }
    );

    // Klick außerhalb des Header-Menüs schließt das aktive Dropdown
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;

        // Wenn der Klick innerhalb von <header-menu> war, ignorieren
        if (this.contains(target)) return;

        // Sonst: offen -> zu
        this.#deactivate();
      },
      { signal: this.#abortController.signal }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately (nur noch auf Klick)
   * @param {MouseEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {MouseEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    // Nur auf echte Klicks reagieren (kein Hover, kein pointerenter)
    if (!event || event.type !== 'click') {
      return;
    }

    if (!(event.target instanceof Element)) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {MouseEvent | FocusEvent} [event]
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // Hover-/pointerleave-Events ignorieren
    if (
      event instanceof MouseEvent &&
      (event.type === 'mouseleave' || event.type === 'mouseout')
    ) {
      return;
    }

    if (event && event.target instanceof Element) {
      const item = findMenuItem(event.target);
      if (item === this.#state.activeItem) {
        this.#debouncedDeactivate();
      }
    } else {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // WICHTIG: immer zum nächstgelegenen Element mit ref="menuitem" hochgehen
  const menuItem = element.closest('[ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu (nur direkt zugehöriges, nicht irgendeins).
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  // zum zugehörigen <li> hoch
  const parentItem = element.closest('.menu-list__list-item');
  if (!parentItem) return null;

  // NUR direkte Kinder dieses List-Items durchsuchen
  const children = Array.from(parentItem.children);
  for (const child of children) {
    if (
      child instanceof HTMLElement &&
      child.getAttribute('ref') === 'submenu[]'
    ) {
      return child;
    }
  }

  return null;
}
